// Format-only payment validation (payment-capture design §3.5, §3.6, D6).
//
// EVERY CHECK IN THIS FILE IS LOCAL, OFFLINE ARITHMETIC OR SHAPE. Nothing here
// contacts a network, and §3.7 lists what that deliberately excludes: no
// authorization, no zero-dollar auth, no account or micro-deposit
// verification, no AVS, no tokenization SDK, no balance or funds check, no
// fraud scoring, no BIN-database lookup. Brand derivation is a compiled-in
// range check, not a service call.
//
// A format-valid instrument that is closed, frozen, empty or fraudulent PASSES
// here and the order is accepted. That is intended: the storefront asserts
// "this is well-formed", never "this will pay". MIS makes the second judgement
// and every failure mode above surfaces there.
//
// THE SERVER RUNS THE SAME CHECKS AND THE SERVER IS THE ONE THAT DECIDES
// (§3.6). This copy exists for immediate feedback; the client is not
// trustworthy, so nothing here may be the only place a rule lives.
//
// NOTE ON TEST DATA: the standard publisher test numbers (4111 1111 1111 1111,
// 5555 5555 5555 4444, 2223 0031 2200 3222) and the published ABA test routing
// number 021000021 are used in tests precisely because they are recognisable
// as non-real. No genuine PAN or account number belongs in this repo.

// -- shared -------------------------------------------------------------------

/** Strip the separators humans type. Nothing else is removed. */
export function normalizeDigits(raw: string): string {
  return raw.replace(/[\s-]/g, "");
}

const DIGITS_ONLY = /^[0-9]+$/;

// -- card ---------------------------------------------------------------------

/**
 * The card brands D1 admits, plus the two outcomes that are NOT a brand:
 * `unsupported` (a well-formed number from a network we do not accept) and
 * `unknown` (not enough digits yet to tell). Keeping those distinct matters —
 * "we don't accept Amex" and "keep typing" are different messages.
 */
export type CardBrand = "visa" | "mastercard";
export type BrandDerivation = CardBrand | "unsupported" | "unknown";

/** Lengths each accepted brand issues. Used by the length check in §3.6. */
const BRAND_LENGTHS: Record<CardBrand, readonly number[]> = {
  visa: [13, 16, 19],
  mastercard: [16],
};

export const ACCEPTED_BRANDS: readonly CardBrand[] = ["visa", "mastercard"];

export function brandLabel(brand: CardBrand): string {
  return brand === "visa" ? "Visa" : "Mastercard";
}

/**
 * Derive the brand from the issuer identification range (§3.5).
 *
 * The number carries the answer unambiguously, so the brand is never asked —
 * a dropdown is a field a customer can get wrong about their own card.
 *
 * Ranges: Visa is a leading 4. Mastercard is 51-55 (the classic range) or
 * 2221-2720 (the 2-series added in 2017); a 2-series check that only looked at
 * "22" would wrongly accept 2200-2220.
 */
export function deriveCardBrand(rawNumber: string): BrandDerivation {
  const digits = normalizeDigits(rawNumber);
  if (!DIGITS_ONLY.test(digits)) return digits.length === 0 ? "unknown" : "unsupported";
  if (digits.length < 4) return "unknown";
  if (digits.startsWith("4")) return "visa";
  const two = Number(digits.slice(0, 2));
  if (two >= 51 && two <= 55) return "mastercard";
  const four = Number(digits.slice(0, 4));
  if (four >= 2221 && four <= 2720) return "mastercard";
  return "unsupported";
}

/**
 * The Luhn checksum. Pure arithmetic over the digits — it proves the number is
 * internally consistent and proves nothing whatsoever about the account.
 */
export function luhnValid(rawNumber: string): boolean {
  const digits = normalizeDigits(rawNumber);
  if (digits.length < 2 || !DIGITS_ONLY.test(digits)) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * Validate a card number's format, returning a customer-facing message or null.
 *
 * Order matters: characters, then brand, then length, then checksum. A number
 * from an unaccepted network should say so rather than failing on a length
 * rule for a brand it does not have.
 *
 * The message NEVER echoes the number back (§3.8).
 */
export function validateCardNumber(rawNumber: string): string | null {
  const digits = normalizeDigits(rawNumber);
  if (digits.length === 0) return "Card number is required.";
  if (!DIGITS_ONLY.test(digits)) return "Card number must contain digits only.";
  const brand = deriveCardBrand(digits);
  if (brand === "unknown") return "Enter the full card number.";
  if (brand === "unsupported") {
    return `We accept ${ACCEPTED_BRANDS.map(brandLabel).join(" and ")} only.`;
  }
  if (!BRAND_LENGTHS[brand].includes(digits.length)) {
    return `That is not the right number of digits for a ${brandLabel(brand)} card.`;
  }
  if (!luhnValid(digits)) return "Check the card number — one or more digits look wrong.";
  return null;
}

/** Group digits in fours as the customer types (§3.8 "formatting as they type"). */
export function formatCardNumber(rawNumber: string): string {
  const digits = normalizeDigits(rawNumber).replace(/[^0-9]/g, "");
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

export interface CardExpiry {
  /** 1-12. */
  month: number;
  /** Four-digit year. */
  year: number;
}

/**
 * Parse an expiry into a real month and year, or null when it is not one.
 *
 * Accepts MM/YY, MM/YYYY and bare MMYY / MMYYYY, because customers type all of
 * them and rejecting a real date over a separator is a hostile way to fail.
 * A two-digit year resolves into the 2000s, which is the only reading that can
 * be correct for a card that has not already expired.
 */
export function parseExpiry(raw: string): CardExpiry | null {
  const digits = raw.replace(/[^0-9]/g, "");
  if (digits.length !== 4 && digits.length !== 6) return null;
  const month = Number(digits.slice(0, 2));
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  const yearPart = digits.slice(2);
  const year = yearPart.length === 2 ? 2000 + Number(yearPart) : Number(yearPart);
  if (!Number.isInteger(year)) return null;
  return { month, year };
}

/**
 * Whether an expiry is in the past relative to `now`. A card expires at the END
 * of its stated month, so the stated month itself is still valid.
 *
 * `now` is injected rather than read from the clock so the check is
 * deterministic in tests (no sleep-based or today-dependent assertions).
 */
export function expiryIsPast(expiry: CardExpiry, now: Date): boolean {
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  return expiry.year < nowYear || (expiry.year === nowYear && expiry.month < nowMonth);
}

export function validateExpiry(raw: string, now: Date): string | null {
  if (raw.trim().length === 0) return "Expiry is required.";
  const parsed = parseExpiry(raw);
  if (!parsed) return "Enter the expiry as MM/YY.";
  if (expiryIsPast(parsed, now)) return "That expiry date has passed.";
  return null;
}

// -- ACH ----------------------------------------------------------------------

export type AchAccountType = "checking" | "savings";
export const ACH_ACCOUNT_TYPES: readonly AchAccountType[] = ["checking", "savings"];

export function isAchAccountType(value: string): value is AchAccountType {
  return (ACH_ACCOUNT_TYPES as readonly string[]).includes(value);
}

/**
 * The ABA position-weighted checksum: digits 1,4,7 weigh 3; 2,5,8 weigh 7;
 * 3,6,9 weigh 1; the total must be divisible by 10. Pure arithmetic on the
 * nine digits — it says the routing number is well-formed, not that the
 * institution exists or is reachable.
 */
export function abaChecksumValid(rawRouting: string): boolean {
  const digits = normalizeDigits(rawRouting);
  if (digits.length !== 9 || !DIGITS_ONLY.test(digits)) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (digits.charCodeAt(i) - 48) * weights[i]!;
  return sum % 10 === 0;
}

export function validateRoutingNumber(rawRouting: string): string | null {
  const digits = normalizeDigits(rawRouting);
  if (digits.length === 0) return "Routing number is required.";
  if (!DIGITS_ONLY.test(digits)) return "Routing number must contain digits only.";
  if (digits.length !== 9) return "Routing number must be exactly 9 digits.";
  if (!abaChecksumValid(digits)) return "Check the routing number — one or more digits look wrong.";
  return null;
}

/** The length range US banks issue. Shape only — no institution is contacted. */
const ACCOUNT_MIN_LEN = 4;
const ACCOUNT_MAX_LEN = 17;

export function validateAccountNumber(rawAccount: string): string | null {
  const digits = normalizeDigits(rawAccount);
  if (digits.length === 0) return "Account number is required.";
  if (!DIGITS_ONLY.test(digits)) return "Account number must contain digits only.";
  if (digits.length < ACCOUNT_MIN_LEN || digits.length > ACCOUNT_MAX_LEN) {
    return `Account number must be between ${ACCOUNT_MIN_LEN} and ${ACCOUNT_MAX_LEN} digits.`;
  }
  return null;
}

/** Non-empty after trimming — the only check §3.6 makes on a name. */
export function validateHolderName(raw: string, label: string): string | null {
  return raw.trim().length === 0 ? `${label} is required.` : null;
}

/**
 * Last four digits, for display only (§3.3 — derived server-side and stored in
 * clear; this client copy exists so the review step can show something before
 * the server answers). Returns "" when there are fewer than four digits, never
 * a padded or partial value that could be mistaken for a real suffix.
 */
export function lastFour(raw: string): string {
  const digits = normalizeDigits(raw).replace(/[^0-9]/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
}
